import {
  BackLink,
  Button,
  Dialog,
  DownloadIcon,
  ExpiryMarker,
  EyeIcon,
  EyeOffIcon,
  MoreMenu,
  Notice,
  ScreenTabs,
  TextLink,
  TrashIcon,
  Well,
  WellControl,
  WellMessage,
  type MenuItem,
} from "@don/design-system";
import type { SessionId } from "@don/wire";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";

import type { SessionKnowledgeView } from "../knowledge/source.js";
import {
  DELETE_CONFIRM_BODY,
  DELETE_CONFIRM_LABEL,
  DELETE_CONFIRM_TITLE,
  DELETE_FAILED,
  DELETE_KEEP_LABEL,
  failedNotice,
  refusedOnSession,
  type RenewalState,
} from "../library/retention.js";
import {
  agedOutCopy,
  dateTimeLabel,
  durationLabel,
  metaExpiry,
  sessionTitle,
  videoIsGone,
  type LibraryRow,
} from "../library/row.js";
import { SESSIONS, navigate } from "../routing/route.js";
import { LeadsNowhere } from "../shell/NotFound.js";
import { ReadingColumn, type KnowledgeWrites } from "./ReadingColumn.js";
import {
  isReading,
  readingOf,
  transcriptExportable,
  transcriptFilename,
  transcriptText,
  type Reading,
  type ReadingPort,
} from "./reading.js";
import { GapList, Scrubber } from "./Scrubber.js";
import { SessionPlayer, WELL_TIME } from "./SessionPlayer.js";
import {
  isTimeline,
  timecode,
  timelineOf,
  type SessionMedia,
  type Timeline,
} from "./timeline.js";

/**
 * The session detail: the player and its screens on the left, the reading column on the
 * right. What is here is everything that decides *what plays and where you are in it*;
 * the summary, knowledge, transcript and notes are `ReadingColumn`'s (issue 202). The head
 * (back link, title, meta line, ⋯ menu with Download transcript and Delete) is here (issue 201).
 *
 * **The reading column is outside every branch below**, deliberately. The derived text
 * outlives the video by a year (decisions §(a)), a refused manifest is a fault of the
 * recording and not of the writing, and a track this Console cannot draw is one screen of
 * several. Each of those is a state where the writing is still readable, so putting the
 * column inside the branch that picks a player would take it away in all three.
 *
 * The shapes this screen has to be able to be, and the reason none of them is the others:
 *
 * - **A session the library does not hold.** Absent, not forbidden, and the copy says what
 *   the list is and stops.
 * - **A session whose video has gone.** The reading layout with no way back to the split
 *   (issue 203): the deleted video's box at the top of the reading column, both dates, and
 *   *no player at all*. A player pointed at deleted media is a broken page, and this state is
 *   neither broken nor an error: it is the ordinary end of a 30-day term with a year of
 *   writing still behind it.
 * - **A session the owner chose to read** (`Hide video`, issue 203, D18): the same reading
 *   layout, `Show video` to return, and an evidence link returns to the split at its moment.
 * - **A session this Console has no media for.** `null` is not an empty timeline. The
 *   read may not have answered, or may have failed (`App.tsx`), so a blank rail would claim
 *   that nothing was recorded rather than say that nothing was read. `read` says which of
 *   the two it is, and they are drawn apart (issue 135).
 * - **A session whose manifest contradicts itself.** `timelineOf` refuses it and the
 *   refusal is shown, because a rail drawn from a manifest that does not add up is a
 *   recording the owner does not have.
 * - **A session with a timeline.** The player, and a scrubber whose every hole is
 *   attributed.
 *
 * The player's own three states are its business and are drawn there, not here: a video
 * still arriving, a video that failed and a video playing must not look alike either, and
 * `SessionPlayer` reports which one it is so this screen can stop claiming that nothing is
 * missing from a recording it cannot show.
 */

/**
 * A session the library does not hold.
 *
 * There are three ways to arrive here and the Console cannot tell them apart. The session
 * may never have existed; it may have been deleted, in which case it is gone rather than
 * hidden; or the employee who recorded it may still be deciding whether to keep it, in
 * which case the API answers absent rather than forbidden, deliberately, because a
 * distinct refusal would itself disclose that a recording exists.
 *
 * So the copy says the session is not in the library and stops (screen-catalog
 * `session-not-found`). It does not say the session does not exist, which claims knowledge
 * the Console does not have, and it does not say where the session is instead, which is the
 * disclosure the refusal exists to prevent.
 */
function AbsentSession({ sessionId }: { sessionId: SessionId }) {
  // Nothing in the text depends on the id, so a nonexistent and a foreign id read the same
  // (P19, issue 199; e2e `session.spec.ts`). The id is only on the test hook.
  return (
    <LeadsNowhere
      data-session-absent={sessionId}
      title="This session isn't in your library"
      body="The link doesn't open a session here. Check the link, or find the session in Sessions."
    />
  );
}

/** One frame every five seconds, the stills path's cadence (architecture §1, research/07). */
const STILLS_INTERVAL_MS = 5000;

/**
 * The stills variant: a lesser artifact, not a broken one (P13).
 *
 * A machine with no hardware encoder records JPEG frames rather than video, and there is
 * nothing continuous to scrub through. So the well head says which of the two it is
 * (`Stills only, no video on this computer`), the controls step rather than drag, and the
 * frame carries where in the stills it is (screen-catalog `session-stills`). The rail is
 * drawn, so the gaps are still here and still attributed: a machine recording stills still
 * locks, still sleeps and is still paused.
 */
function StillsWell({
  timeline,
  positionMs,
  onSeek,
  date,
  person,
}: {
  timeline: Timeline;
  positionMs: number;
  onSeek(positionMs: number): void;
  date: string;
  person: string;
}) {
  const frames = Math.max(1, Math.floor(timeline.durationMs / STILLS_INTERVAL_MS) + 1);
  const frame = Math.min(frames, Math.floor(positionMs / STILLS_INTERVAL_MS) + 1);
  const step = (by: number) =>
    onSeek(Math.min(Math.max(0, (frame - 1 + by) * STILLS_INTERVAL_MS), timeline.durationMs));
  return (
    <Well
      head={
        <span data-stills-head="" style={{ color: "var(--well-ink)" }}>
          Stills only, no video on this computer
        </span>
      }
      headEnd={date}
      frame={
        <div data-stills-frame={frame} style={{ position: "absolute", inset: 0 }}>
          <span
            data-stills-tag=""
            style={{
              position: "absolute", left: 10, bottom: 10, padding: "3px 8px",
              borderRadius: "var(--radius-focus)",
              background: "color-mix(in srgb, var(--well) 78%, transparent)",
              // The dark chip reads over a still; on a frame with no image behind it (the
              // wire carries none yet) it is the well's own color, so a hairline edge keeps
              // it a chip rather than loose text.
              boxShadow: "inset 0 0 0 var(--border-hairline) var(--well-mute)",
              color: "var(--well-head)", fontSize: "var(--type-small-size)",
            }}
          >
            {`Still ${frame} of ${frames}, one every ${STILLS_INTERVAL_MS / 1000} s`}
          </span>
        </div>
      }
      controls={
        <>
          <WellControl kind="previous" onClick={() => step(-1)} />
          <WellControl kind="next" onClick={() => step(1)} />
          <span data-transport-readout data-elapsed="" style={WELL_TIME}>
            {`${timecode(positionMs)} / ${timecode(timeline.durationMs)}`}
          </span>
          <Scrubber timeline={timeline} positionMs={positionMs} onSeek={onSeek} person={person} interactive={false} />
        </>
      }
      below={<GapList timeline={timeline} person={person} />}
    />
  );
}

/**
 * The meta line under the title (D22, D32, PRD stories 33 and 34; variant E `.ve-meta`): person,
 * date, length, then the video's date at the row's 10 and 5 day treatment (`metaExpiry`: the
 * hourglass at 10 days or fewer, the red clock at 5 or fewer, drawn as text because nothing here
 * expands) and `Renew` as an accent link when `renew` is given. 14px ink-2, items 18 apart.
 */
function MetaLine({
  sessionId,
  row,
  nowMs,
  date,
  duration,
  renew,
}: {
  sessionId: SessionId;
  row: LibraryRow;
  nowMs: number;
  date: string;
  duration: string | null;
  renew?: { onClick: () => void; disabled: boolean };
}) {
  const expiry = metaExpiry(nowMs, row);
  return (
    <div
      data-session-meta=""
      style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 18,
        rowGap: "var(--space-05)", fontSize: "var(--type-small-size)",
        lineHeight: "var(--type-small-line)", color: "var(--ink-2)",
      }}
    >
      <span>{row.person}</span>
      <span>{date}</span>
      {/* A length, so marked as elapsed rather than read as a clock time. */}
      {duration !== null && <span data-elapsed="">{duration}</span>}
      {expiry.level === "soon" || expiry.level === "urgent" ? (
        <ExpiryMarker level={expiry.level} plain data-meta-expiry="" data-expiry-level={expiry.level}>
          {expiry.text}
        </ExpiryMarker>
      ) : (
        <span data-meta-expiry="" data-expiry-level={expiry.level}>
          {expiry.text}
        </span>
      )}
      {renew !== undefined && (
        <TextLink onClick={renew.onClick} disabled={renew.disabled} data-detail-renew={sessionId}>
          Renew
        </TextLink>
      )}
    </div>
  );
}

/**
 * What the head says when a renewal or a delete did not happen, under the meta line (screen
 * catalog `session-renew-refused`; design-spec §4 "Exceptions": inline, nothing floats). Nothing
 * while either is in flight: the date above has not moved, and a line claiming an outcome nobody
 * has been told about is what these exist to avoid.
 *
 * - Refused: info, `Not renewed.` and the day the video is still kept until, `Try again`, ×.
 * - Failed to arrive: the library's one-video sentence (`failedNotice`), `Try again`.
 * - Delete failed: the session is still here (decisions §(b)), `Try again` reopens the confirm.
 */
function HeadNotice({
  row,
  renewal,
  deleteFailed,
  onRenewAgain,
  onDismissRenewal,
  onDeleteAgain,
  onDismissDelete,
}: {
  row: LibraryRow;
  renewal: RenewalState;
  deleteFailed: boolean;
  onRenewAgain?: () => void;
  onDismissRenewal: () => void;
  onDeleteAgain: () => void;
  onDismissDelete: () => void;
}) {
  const again = (onClick: () => void) => ({ children: "Try again", onClick });
  let kind: string;
  let notice: ReactNode;
  if (deleteFailed) {
    kind = "delete-failed";
    notice = (
      <Notice tone="failed" action={again(onDeleteAgain)} onClose={onDismissDelete}>
        <strong style={{ fontWeight: "var(--type-body-weight-strong)" }}>{DELETE_FAILED.heading}</strong>{" "}
        {DELETE_FAILED.body}
      </Notice>
    );
  } else if (renewal.kind === "refused" || renewal.kind === "failed") {
    kind = renewal.kind;
    const copy = renewal.kind === "refused" ? refusedOnSession(row) : failedNotice([row], 0);
    notice = (
      <Notice
        tone={renewal.kind === "refused" ? "info" : "failed"}
        action={onRenewAgain === undefined ? undefined : again(onRenewAgain)}
        onClose={onDismissRenewal}
      >
        <strong style={{ fontWeight: "var(--type-body-weight-strong)" }}>{copy.heading}</strong> {copy.body}
      </Notice>
    );
  } else {
    return null;
  }
  return (
    <div data-session-retention-notice={kind} style={{ marginTop: "var(--space-15)", maxWidth: 760 }}>
      {notice}
    </div>
  );
}

/** The transcript, handed to the browser as a `.txt` file (the ⋯ menu's `Download transcript`). */
function downloadTranscript(reading: Reading) {
  const blob = new Blob([transcriptText(reading)], { type: "text/plain;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = transcriptFilename(reading);
  anchor.click();
  URL.revokeObjectURL(href);
}

/**
 * The owner delete's confirmation (screen catalog `session-delete`), opened from the ⋯ menu.
 *
 * `Dialog` owns the modal behavior (issue 190): it opens in the top layer over the scrim, so
 * it sits above the playing video, takes focus onto `Keep it`, closes on Escape as the same
 * act, keeps Tab inside itself and gives focus back to the ⋯ that opened it. The destructive
 * action is the outline, the way out is the primary.
 */
function ConfirmDelete({
  sessionId,
  onKeep,
  onConfirm,
}: {
  sessionId: SessionId;
  onKeep: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      title={DELETE_CONFIRM_TITLE}
      onClose={onKeep}
      destructive={{
        children: DELETE_CONFIRM_LABEL,
        onClick: onConfirm,
        "data-confirm-delete": sessionId,
      }}
      safe={{ children: DELETE_KEEP_LABEL, onClick: onKeep, "data-keep-recording": sessionId }}
    >
      {DELETE_CONFIRM_BODY}
    </Dialog>
  );
}

export function SessionScreen({
  sessionId,
  row,
  media,
  reading,
  nowMs,
  onRenew,
  onDelete,
  renewal = { kind: "idle" },
  read = "reading",
  onRetry,
  knowledge = "none",
  workflows,
  knowledgeWrites,
  onRetryKnowledge,
  initialAtMs,
}: {
  sessionId: SessionId;
  row: LibraryRow | undefined;
  media: SessionMedia | null;
  reading: ReadingPort;
  nowMs: number;
  /**
   * Renew this session, or `undefined` where there is nowhere to send a renewal.
   *
   * The same function the library's inline Renew is (issue 30): `App` owns it, so the two
   * controls are two call sites of one path rather than two implementations.
   */
  onRenew?: (sessionId: string) => void;
  /**
   * Destroy this session, or `undefined` where there is nowhere to send a deletion.
   *
   * It rejects when the deletion did not happen, and the confirmation below is what says so:
   * the row is removed only once the server has answered, because the UI never claims a
   * deletion it has not performed (decisions §(b)).
   */
  onDelete?: (sessionId: string) => Promise<void>;
  /** What happened to the last renewal, so the header can say when one did not happen. */
  renewal?: RenewalState;
  /** Where the session read is while `media` is `null`. Not read once `media` is set. */
  read?: SessionRead;
  /** Run the session read again. Offered only on a failed read. */
  onRetry?: () => void;
  /** The side column's Knowledge and Workflows (issue 202, decision 38): `none` with no source. */
  knowledge?: SessionKnowledgeView;
  /** Workflows, where it differs from `knowledge` (issue 229; `ReadingColumn`'s `workflows`). */
  workflows?: SessionKnowledgeView;
  /** Keep and Discard on a draft, or `undefined` where the source offers no write (issue 204). */
  knowledgeWrites?: KnowledgeWrites;
  /** Ask the knowledge source again after a failed read (issue 204). */
  onRetryKnowledge?: () => void;
  /**
   * Where the session opens, from the URL's `?at=` (issue 280): the position and the seek start
   * here, and start here again when `sessionId` changes. `undefined` opens at the start.
   */
  initialAtMs?: number;
}) {
  const [trackIndex, setTrackIndex] = useState(0);
  const [positionMs, setPositionMs] = useState(initialAtMs ?? 0);
  // Whether the picture is playing, as the player reports it, so the side column tones and
  // follows the current line only then (F1).
  const [playing, setPlaying] = useState(false);
  // `Try again` on a video that did not load: a new player, which asks for the playlist again.
  const [attempt, setAttempt] = useState(0);
  // The confirmation's own two states. Local rather than on `App`, because they are about
  // this dialog: whether it is open, and whether the last attempt through it failed.
  const [confirming, setConfirming] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  // **In flight, which is the state the less reversible of the two actions was missing.**
  // The confirmation closed before the await and nothing else changed, so an owner on a slow
  // link saw the dialog vanish and nothing happen, and the obvious next move issued a second
  // destroy. This is the shape the renewal above already has: the control that was pressed
  // stands down until the server answers.
  const [deleting, setDeleting] = useState(false);
  const header = useRef<HTMLDivElement>(null);
  // A seek the screen asked for, told apart from a position the player reported by a token
  // that only a request moves. Without the distinction every `timeupdate` would read as a
  // fresh instruction to seek the element to where it already is.
  const [seek, setSeek] = useState({ positionMs: initialAtMs ?? 0, token: 0 });
  // Hide video (issue 203): the reading layout, the player unmounted so nothing plays unseen.
  const [hidden, setHidden] = useState(false);
  // `App` keeps this screen mounted across a back or forward between two sessions, so the
  // choice is reset when the session changes: a session opens split (issue 203 rework), and at
  // its own `initialAtMs` rather than where the last one was (issue 280).
  const [hiddenFor, setHiddenFor] = useState(sessionId);
  if (hiddenFor !== sessionId) {
    const at = initialAtMs ?? 0;
    setHiddenFor(sessionId);
    setHidden(false);
    setPositionMs(at);
    setSeek((held) => ({ positionMs: at, token: held.token + 1 }));
  }

  const timeline = useMemo(
    () => (media === null ? null : timelineOf(media, trackIndex)),
    [media, trackIndex],
  );
  // The transcript the ⋯ menu offers to download (issue 201), or `null` when there is none to
  // offer: not read, refused by `readingOf`, absent or empty.
  const transcript = useMemo(() => {
    const built = reading === null || reading === "failed" ? null : readingOf(reading);
    return built !== null && isReading(built) && transcriptExportable(built) ? built : null;
  }, [reading]);

  // **Focus, on the one path `Dialog`'s own restore cannot take.** That restore is
  // skipped when the control that opened the confirmation has stood down, which confirming
  // is what does (the ⋯ that opened it, issue 201), so on a delete that failed the dialog
  // went, the trigger came back and the ring was left on `<body>`: a keyboard owner who
  // confirmed the product's one irreversible action, and had it not happen, tabbed from the
  // top of the document to reach the control they were just on. Queried rather than held on
  // a ref, which is how this file moves focus into something it has just rendered.
  useEffect(() => {
    if (!deleteFailed) return;
    header.current?.querySelector<HTMLElement>("[data-session-menu]")?.focus();
  }, [deleteFailed]);

  // A dismissed notice is the renewal state it was dismissed on (issue 195's rule): a new
  // outcome is a new object, so it shows again.
  const [dismissed, setDismissed] = useState<RenewalState | null>(null);

  // The browser tab names the session the way the heading does (issue 191), and goes back
  // to the document's own title when this screen does.
  const tabTitle = row === undefined ? null : sessionTitle(row);
  useEffect(() => {
    if (tabTitle === null) return;
    const before = document.title;
    document.title = tabTitle;
    return () => {
      document.title = before;
    };
  }, [tabTitle]);

  if (row === undefined) return <AbsentSession sessionId={sessionId} />;

  const requestSeek = (to: number) => {
    setPositionMs(to);
    setSeek((held) => ({ positionMs: to, token: held.token + 1 }));
  };

  // Switching screens re-asks for the position the owner is already at, and that is what
  // keeps it: the other track is a different playlist, so the element has to be told where
  // to land in it. It stays the same element throughout.
  const switchScreen = (index: number) => {
    setTrackIndex(index);
    setSeek((held) => ({ positionMs, token: held.token + 1 }));
  };

  const gone = videoIsGone(nowMs, row);
  // Hide video is offered only over a picture to hide: a timeline the well draws, as video or
  // stills. A media read not answered or failed, or a timeline refused, is a message in the
  // well, which hiding would only take out of sight (issue 203 rework).
  const canHide = !gone && timeline !== null && isTimeline(timeline);
  // The reading layout (design-spec §3 "Hide-video mode"): chosen, or the only one left.
  const hiding = hidden && canHide;
  const readingMode = hiding || gone;
  // Back to the split. The player is new, so it is told where to land: the position the owner
  // left, or the moment an evidence link names (D18).
  const showVideo = (at: number) => {
    setHidden(false);
    requestSeek(at);
  };
  const hideVideo = () => {
    setPlaying(false);
    setHidden(true);
  };
  const track = media?.tracks.find((each) => each.index === trackIndex);
  const stills = track?.kind === "stills";
  const trackLabel = track?.label ?? `Screen ${trackIndex + 1}`;
  const duration = durationLabel(row);
  const date = dateTimeLabel(row.startedAtMs);
  // The well head's left: the picker when the recorder captured several screens (selected in
  // well-ink, design-spec §4), else the one screen's name.
  const screenHead =
    media !== null && media.tracks.length > 1 ? (
      <ScreenTabs
        screens={media.tracks.map((each) => each.label)}
        value={trackIndex}
        onChange={switchScreen}
      />
    ) : (
      <span>{trackLabel}</span>
    );

  // **The ⋯ menu** (D22, D37, PRD module 9): `Download transcript`, then a rule and `Delete
  // session` last. Each only when it can act: a transcript to download, somewhere to send a
  // deletion (the deployed Console has no retention source, story 57). No Copy link (D37).
  const menuItems: MenuItem[] = [];
  if (transcript !== null) {
    menuItems.push({
      id: "download-transcript",
      label: "Download transcript",
      icon: <DownloadIcon size={17} />,
      onSelect: () => downloadTranscript(transcript),
    });
  }
  if (onDelete !== undefined) {
    menuItems.push({
      id: "delete-session",
      label: "Delete session",
      icon: <TrashIcon />,
      ruleBefore: menuItems.length > 0,
      onSelect: () => {
        setDeleteFailed(false);
        setConfirming(true);
      },
    });
  }

  return (
    <div
      data-session-detail={sessionId}
      // `position: relative` because `Dialog` positions itself against the nearest
      // positioned ancestor, and the confirmation belongs over this screen rather than over
      // whatever happens to be positioned above it.
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* **The head** (design-spec §3 "Session detail", variant E `.ve-sd-head`): back link,
          title at display size, the meta line, and the actions right. Padding 14 above and 20
          below inside the page's 40 sides; no rule under it, the body's well and sheet are what
          separate it (§1, separation by space and tone). */}
      <div
        ref={header}
        data-session-head=""
        style={{
          flex: "none",
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--space-3)",
          padding: "14px var(--space-5) var(--space-25)",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: "var(--space-1)" }}>
            <BackLink href={SESSIONS} onNavigate={() => navigate(SESSIONS)}>
              Sessions
            </BackLink>
          </div>
          <h1
            data-session-title=""
            style={{
              margin: "0 0 var(--space-1)",
              fontSize: "var(--type-display-size)",
              lineHeight: "var(--type-display-line-session)",
              fontWeight: "var(--type-display-weight)",
              letterSpacing: "var(--type-display-tracking)",
              color: "var(--ink-1)",
              overflowWrap: "anywhere",
            }}
          >
            {sessionTitle(row)}
          </h1>
          <MetaLine
            sessionId={sessionId}
            row={row}
            nowMs={nowMs}
            date={date}
            duration={duration}
            // **Renew only with somewhere to send it, and only while there is a video to keep**
            // (decisions §(d): renewal cannot revive a gone video). An accent link, D32. Down
            // while a renewal or a destroy is in flight: it prints the date the server chose,
            // and offering it against a session whose destruction is outstanding is how the
            // header came to say "45 days" about a session already on its way out.
            renew={
              !gone && onRenew !== undefined
                ? { onClick: () => onRenew(sessionId), disabled: renewal.kind === "working" || deleting }
                : undefined
            }
          />
          <HeadNotice
            row={row}
            renewal={renewal === dismissed ? { kind: "idle" } : renewal}
            deleteFailed={deleteFailed}
            onRenewAgain={onRenew === undefined ? undefined : () => onRenew(sessionId)}
            onDismissRenewal={() => setDismissed(renewal)}
            onDeleteAgain={() => {
              setDeleteFailed(false);
              setConfirming(true);
            }}
            onDismissDelete={() => setDeleteFailed(false)}
          />
        </div>
        {(menuItems.length > 0 || canHide) && (
          <div style={{ flex: "none", marginTop: "var(--space-4)", display: "flex", gap: "var(--space-1)" }}>
            {/* Hide or show the video (issue 203). Not offered once the video is deleted,
                nor while the well holds no picture (`canHide`). */}
            {canHide && (
              <Button
                variant="secondary"
                data-video-toggle={hiding ? "show" : "hide"}
                iconLeft={hiding ? <EyeIcon /> : <EyeOffIcon />}
                onClick={() => (hiding ? showVideo(positionMs) : hideVideo())}
              >
                {hiding ? "Show video" : "Hide video"}
              </Button>
            )}
            {menuItems.length > 0 && (
              <MoreMenu label="Session actions" data-session-menu={sessionId} disabled={deleting} items={menuItems} />
            )}
          </div>
        )}
      </div>

      {/* **The confirmation, which is this issue's criterion and not a contradiction of
          decisions §(d).** That decision removes the confirmation from *renewal*, which is
          reversible and which the owner may do as often as they like. This is the other
          direction and is not reversible. */}
      {onDelete !== undefined && confirming && (
        <ConfirmDelete
          sessionId={sessionId}
          onKeep={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            setDeleting(true);
            void onDelete(sessionId).catch(() => {
              setDeleting(false);
              setDeleteFailed(true);
            });
          }}
        />
      )}

      {/* **The body: `55fr 45fr`, gap 48, padding 0 40** (design-spec §3, decision 18). The
          template, the rows and the overflow are indirected (ADR 0036 clauses 8 and 9): the
          wide values are fallbacks, the narrow ones local properties, and only the
          `data-layout="session-split"` width query in `interactions.css` re-points them, so
          below 1000px the well stacks above the side column and the body scrolls down, never
          sideways (e2e `session.spec.ts`, issue 200 split). In the reading layout (issue 203)
          the wide template is `200px minmax(0,760px)` centered, gap 56, and the same narrow
          rule stacks the links above the sheet (e2e `reading.spec.ts`, issue 203). */}
      <div
        data-layout="session-split"
        data-session-split=""
        data-session-mode={readingMode ? "reading" : "split"}
        style={
          {
            "--split-cols-narrow": "minmax(0, 1fr)",
            // `max-content`, not `auto`: the cell's `minHeight: 0` makes its automatic
            // minimum zero, and an `auto` row in a body of fixed height shrank under the well.
            "--split-rows-narrow": "max-content minmax(480px, 1fr)",
            "--split-overflow-narrow": "auto",
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: readingMode
              ? "var(--split-cols, 200px minmax(0, 760px))"
              : "var(--split-cols, minmax(0, 55fr) minmax(0, 45fr))",
            gridTemplateRows: "var(--split-rows, minmax(0, 1fr))",
            justifyContent: "center",
            columnGap: readingMode ? "var(--space-7)" : "var(--space-6)",
            rowGap: "var(--space-3)",
            padding: "0 var(--space-5)",
            overflowY: "var(--split-overflow, hidden)" as CSSProperties["overflowY"],
            overflowX: "hidden",
          } as CSSProperties
        }
      >
        {!readingMode && (
          <div
            style={{
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-15)",
              paddingTop: "var(--space-2)",
            }}
          >
            {/* One condition and not two, because it is one fact: the memo below computes
                from `media`, so it is `null` exactly when `media` is. Asking twice left an
                inner `timeline === null` branch that nothing could reach, and an
                unreachable branch reads as a live path to whoever edits here next. */
            media === null || timeline === null ? (
              <MediaUnread read={read} onRetry={onRetry} date={date} />
            ) : !isTimeline(timeline) ? (
              /* **The picker stays in the well head when one screen is refused.** A manifest
                 whose screen 2 does not add up used to replace the whole session detail and
                 take the screen tabs with it, so an owner watching screen 1 who clicked screen
                 2 had no control left on the page that went back to the screen that was
                 playing. The tabs come from `media.tracks`, which is readable whether or not
                 any one track's timeline is. */
              <Well
                head={screenHead}
                headEnd={date}
                frame={
                  <UnplayableTimeline
                    fault={timeline.fault}
                    detail={timeline.detail}
                    alone={media.tracks.length <= 1}
                  />
                }
              />
            ) : stills ? (
              <StillsWell
                timeline={timeline}
                positionMs={positionMs}
                onSeek={requestSeek}
                date={date}
                person={row.person}
              />
            ) : (
              <SessionPlayer
                // Keyed by session, so what the player remembers about the recording, which
                // fragments of it are holes, outlives every playlist swap inside one session
                // and none across two. `Try again` is a new attempt, and a new player.
                key={`${media.sessionId}#${attempt}`}
                timeline={timeline}
                seek={seek}
                positionMs={positionMs}
                onPosition={setPositionMs}
                screen={screenHead}
                date={date}
                below={<GapList timeline={timeline} person={row.person} />}
                onPlaying={setPlaying}
                onRetry={() => setAttempt((count) => count + 1)}
              >
                <Scrubber
                  timeline={timeline}
                  positionMs={positionMs}
                  onSeek={requestSeek}
                  person={row.person}
                />
              </SessionPlayer>
            )}
          </div>
        )}

        {/* Outside every branch above: the writing outlives the video by a year, and a
            recording the Console cannot draw is still a session the owner can read. Seeking
            is what goes when the video does. */}
        <ReadingColumn
          // A failed session read fails the side column too: one read answers both (issue 202).
          reading={reading === null && media === null && read === "failed" ? "failed" : reading}
          positionMs={positionMs}
          // Hidden, a link brings the video back at its moment (D18).
          onSeek={gone ? null : hiding ? showVideo : requestSeek}
          playing={playing && !readingMode}
          gaps={timeline !== null && isTimeline(timeline) ? timeline.gaps : []}
          person={row.person}
          knowledge={knowledge}
          workflows={workflows}
          knowledgeWrites={knowledgeWrites}
          onRetryKnowledge={onRetryKnowledge}
          videoPlays={media !== null && !gone}
          onRetry={onRetry}
          mode={readingMode ? "reading" : "split"}
          banner={gone ? <VideoDeleted row={row} nowMs={nowMs} /> : undefined}
        />
      </div>
    </div>
  );
}

/**
 * The deleted video's box at the top of the reading column (screen catalog
 * `session-video-deleted`, issue 203): the day it went, then what stays and until when.
 */
function VideoDeleted({ row, nowMs }: { row: LibraryRow; nowMs: number }) {
  const copy = agedOutCopy(nowMs, row);
  return (
    <div data-video-aged-out="">
      <Notice tone="absent">
        <strong style={{ display: "block", fontWeight: "var(--type-body-weight-current)" }}>{copy.heading}</strong>{" "}
        <span style={{ color: "var(--ink-2)" }}>{copy.body}</span>
      </Notice>
    </div>
  );
}

/**
 * Where the session read is while `media` is `null` (issue 135).
 *
 * `App.tsx`'s session effect sets `reading` before each read and `failed` when it rejects
 * with anything but a 401. A 401 goes to sign-in instead (`backend/adapter.ts`
 * `onUnauthorized`), so no failure is drawn for it.
 */
export type SessionRead = "reading" | "failed";

/** What the media panel says while the session read is in flight. Exported for tests. */
export const SESSION_READING_LINE = "Reading this recording from the server";

/** What the media panel says when the session read failed. Exported for tests. */
export const SESSION_UNREADABLE_LINE = "This recording could not be read from the server";

/**
 * No media, which is not an empty session.
 *
 * `media` is `null` until the session read answers, and stays `null` when it fails
 * (`App.tsx`, the session effect). So this says which of the two is true rather than drawing
 * an empty rail, which would say that nothing was recorded. The two must not look alike: an
 * owner waiting and an owner looking at a failure have different next moves, and only the
 * failure offers one.
 */
function MediaUnread({
  read,
  onRetry,
  date,
}: {
  read: SessionRead;
  onRetry?: () => void;
  date: string;
}) {
  // The well with no picture, the player's own loading and failed shapes (issue 200), so a
  // session whose media is unread and a video still arriving read as one family.
  const frame =
    read === "reading" ? (
      <WellMessage kind="loading" title={SESSION_READING_LINE} data-player-absent="reading" role="status" />
    ) : (
      <WellMessage
        kind="failed"
        title={SESSION_UNREADABLE_LINE}
        body="Nothing on this page says what was recorded, because the read that would say it did not answer."
        data-player-absent="failed"
        role="alert"
        action={
          onRetry !== undefined ? (
            <Button variant="secondary" size="sm" data-session-retry="" onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      />
    );
  return <Well headEnd={date} frame={frame} />;
}

/**
 * A timeline `timelineOf` refused, for one screen.
 *
 * Shown rather than swallowed. Every refusal is a case where the manifest contradicts
 * itself: a hole nobody attributed, a reason outside the recorder's vocabulary, two
 * epochs claiming the same stretch of time. Drawing a rail from one would show the owner
 * a recording that is not the recording they have.
 *
 * **It names the screen, and it stands where the player stands.** `timelineOf` is asked
 * per track, so one screen of a multi-screen session can be refused while the others play;
 * this panel used to replace the whole session detail and remove the screen tabs with it,
 * which left an owner who clicked a bad screen with no control on the page that went back
 * to the good one. The way back is the tabs, so the button is offered only when there are
 * no tabs to offer.
 */
function UnplayableTimeline({
  fault,
  detail,
  alone,
}: {
  fault: string;
  detail: string;
  alone: boolean;
}) {
  return (
    // `detail` is on the element rather than in the sentence. It is written for whoever
    // fixes the producer, so it names instants in milliseconds and fields by their port
    // spelling, and `epoch 0 spans 0ms-84000ms, outside 0ms-24000ms` is not something an
    // owner can act on: every other duration on this screen is a timecode. The frame says
    // what happened, the attribute carries the arithmetic.
    <WellMessage
      kind="failed"
      data-timeline-fault={fault}
      data-timeline-fault-detail={detail}
      title="This screen can't be shown."
      body="Its timeline doesn't add up. What was recorded is unaffected."
      action={
        alone ? (
          <Button variant="secondary" size="sm" onClick={() => navigate(SESSIONS)}>
            Back to Sessions
          </Button>
        ) : undefined
      }
    />
  );
}
